import { Box } from "@mui/material";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { SEO } from "../../../components/seo";
import Button from "../../../components/ui/Button";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { getGroupSEO } from "../../../constants/seo";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../contexts/ToastContext";
import apiService from "../../../services/api";
import { DeleteGroupDialog, GroupHeader, Tabs } from "./parts";

export const ViewGroup = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { showToast } = useToast();
  const { currentUser } = useAuth();

  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isCurrentUserHost = currentUser?.id === groupData?.created_by;

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteGroup = async () => {
    try {
      const response = await apiService.deleteGroup(groupId);

      if (response.success) {
        showToast("Group deleted successfully", "success");
        navigate("/dashboard");
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setShowDeleteDialog(false);
    }
  };

  useEffect(() => {
    const getGroupData = async () => {
      setLoading(true);
      try {
        const response = await apiService.fetchGroupById(groupId);
        if (response.success) {
          setGroupData(response.data);
        } else {
          showToast("Something went wrong", "error");
        }
      } catch (error) {
        showToast(error.error, "error");
      } finally {
        setLoading(false);
      }
    };
    getGroupData();
  }, [groupId]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!groupData) {
    return <div>Group not found</div>;
  }

  const groupSEO = getGroupSEO(groupData);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 0, md: 4 },
        alignItems: { xs: "stretch", md: "flex-start" },
      }}
    >
      <SEO
        title={groupSEO.title}
        description={groupSEO.description}
        keywords={groupSEO.keywords}
      />
      {/*Group Details and Content */}
      <Box
        sx={{
          flex: { xs: "none", md: 1 },
          backgroundColor: { xs: "transparent", md: "white" },
          borderRadius: { xs: 0, md: "8px" },
          boxShadow: { xs: "none", md: "0 2px 8px rgba(0,0,0,0.1)" },
          // overflow: "hidden",
        }}
      >
        {/* Group Header */}
        <GroupHeader groupData={groupData} />

        {/* Content Area with Tabs */}
        <Tabs />
      </Box>

      {/* Action Buttons */}
      {isCurrentUserHost && (
        <Box
          sx={{
            position: { xs: "fixed", md: "static" },
            display: "flex",
            flexDirection: { xs: "column", sm: "row", md: "column" },
            gap: 2,
            p: 2,
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            minWidth: { xs: "auto", md: "200px", lg: "272px" },
            bottom: { xs: 0, md: "auto" },
            left: { xs: 0, md: "auto" },
            right: { xs: 0, md: "auto" },
            zIndex: 10,
          }}
        >
          <Button
            component={Link}
            to={`/group/${groupId}/edit`}
            variant="primary"
            startIcon={<IconEdit size={16} />}
            sx={{
              fontSize: "16px",
              py: 1.5,
            }}
          >
            Edit group
          </Button>
          <Button
            variant="secondary"
            startIcon={<IconTrash size={16} />}
            onClick={handleOpenDeleteDialog}
            sx={{
              fontSize: "16px",
              py: 1.5,
              color: "#FF4D4F",
              borderColor: "#FF4D4F",
              "&:hover": {
                backgroundColor: "#FFF2F0",
                borderColor: "#FF4D4F",
              },
            }}
          >
            Delete group
          </Button>
        </Box>
      )}
      {/* Delete Dialog */}
      <DeleteGroupDialog
        groupName={groupData.name}
        open={showDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onDelete={handleDeleteGroup}
      />
    </Box>
  );
};
